<template>
  <div class="renew-modal-payment__container">
    <div
      :class="[
        'renew-modal-payment__header',
        isAddCreditCardForm && 'renew-modal-payment__header--hide',
      ]"
    >
      <h2 class="renew-modal-payment__title">
        {{ modalTitle }}
      </h2>
      <div class="renew-modal-payment__info-wrapper">
        <div
          v-if="isRenewalAvailableHigherCertComp"
          class="renew-modal-payment__info-higher-cert"
        >
          <div>
            You’ve earned a more advanced certification in the career path. Pay
            the renewal fee to extend your
            {{ renewalModalStatus.certification.short_name }} certification by 3
            years from its current expiration date.
          </div>
          <div>
            Expiration date after renewal: {{ renewalModalStatus.renewalDate }}
          </div>
        </div>
        <div
          v-if="
            renewalModalStatus.certification &&
              !isRenewalAvailableHigherCertComp &&
              voucher === null
          "
          class="renew-modal-payment__info"
        >
          You’re submitting
          {{ renewalModalStatus.certification.cpe_credits_needed }} credits to
          renew your {{ renewalModalStatus.certification.short_name }}.
        </div>
        <div
          v-if="
            renewalModalStatus.certification &&
              !isRenewalAvailableHigherCertComp
          "
          class="renew-modal-payment__info--greyed"
        >
          Expiration date after renewal: {{ renewalModalStatus.renewalDate }}
        </div>
        <div
          v-if="isRenewalAvailableHigherCertComp"
          class="renew-modal-payment__info-fee"
        >
          <div class="renew-modal-payment__info-fee-legend">Renewal fee</div>
          <div class="renew-modal-payment__info-fee-amount">
            <div class="renew-modal-payment__info-fee-amount-subtitle">
              Subtotal
            </div>
            <div class="renew-modal-payment__info-fee-amount">
              {{ renewalFeeSubtotal }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="renew-modal-payment__payment-flow">
      <PaymentMethodList
        v-if="paymentMethodOnFile"
        @update:show-add-credit-card-form="isAddCreditCardFormHandler($event)"
        @update:creating-paypal-instance="isCreatingPaypalInstance($event)"
        @existing-payment-clicked="handlePaymentMethodChange(false)"
        @replace-payment-clicked="handlePaymentMethodChange(true)"
      />
    </div>
    <div
      :class="[
        'renew-modal-payment__footer',
        isAddCreditCardForm && 'renew-modal-payment__footer--hide',
      ]"
    >
      <ModalGenericCartFooter
        show-coupon-input
        show-back-btn
        paddingSum="60"
        class="renew-modal-payment__cart"
        :showItemsPill="false"
        :cartTotalItems="1"
        :cartTotalAmount="totalPrice"
        :disableBuyBtn="buyActionDisabled"
        :selectedProduct="voucher"
        :primaryBtnText="cartFooterPrimaryBtnText"
        :isLoadingProp="isLoading"
        :cartTaxAmount="totalTax"
        @back-action="goBack"
        @buy-action="onBuyAction"
      />
      <CaptchaBlock
        ref="invisibleRecaptcha"
        keyType="subscriptions"
        @captcha-verify-action="submitOrder"
      />
      <div v-show="threeDSecure" id="recurly-3ds"></div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import PaymentMethodList from '@/components/forms/payments/PaymentMethodList.vue'
import ModalGenericCartFooter from '@/components/modals/ModalGenericCartFooter.vue'
import CaptchaBlock from '@/components/shared/CaptchaBlock.vue'
import CONSTANTS from '@/constants'
import { certificationCommons, paymentCommons } from '@/helpers/mixins'

const {
  CPE: { STEPS },
} = CONSTANTS

export default {
  name: 'RenewModalPayment',
  components: { PaymentMethodList, ModalGenericCartFooter, CaptchaBlock },
  mixins: [certificationCommons, paymentCommons],
  data() {
    return {
      totalPrice: 0,
      totalTax: 0,
      buyActionDisabled: false,
      isAddCreditCardForm: false,
      isLoading: false,
      renewalAvailability: {},
    }
  },
  computed: {
    ...mapState({
      paymentMethodOnFile: state => state.subscriptions.paymentInfo,
    }),
    ...mapGetters({
      renewalModalStatus: 'cpeProgram/cpeRenewModalStatus',
      certRenewalCost: 'certifications/certRenewalCost',
      userPaymentMethod: 'subscriptions/userPaymentMethod',
      billingToken: 'subscriptions/getBillingToken',
      certRenewalPurchaseGetter: 'certifications/certRenewalPurchase',
    }),
    renewalFeeSubtotal() {
      return this.certRenewalCost && this.certRenewalCost.due_today
        ? `$${this.certRenewalCost.due_today.subtotal.toFixed(2)}`
        : 'Loading...'
    },
    modalTitle() {
      return this.renewalModalStatus.isRenewal
        ? 'Renew certification'
        : 'Purchase exam voucher'
    },
    cartFooterPrimaryBtnText() {
      return this.renewalModalStatus.isRenewal ? 'Pay and renew' : 'Buy now'
    },
    certification() {
      return this.renewalModalStatus.certification
    },
    voucher() {
      return this.renewalModalStatus.voucher
    },
    isRenewalAvailableHigherCertComp() {
      return this.isRenewalAvailableHigherCert
    },
  },
  watch: {
    userPaymentMethod: {
      immediate: true,
      handler(newVal) {
        this.buyActionDisabled = !newVal
      },
    },
    submitOrderResult: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.invoice_number) {
          this.setRenewModalStatus({
            step: STEPS.SUCCESS,
            currentSelection: this.voucher,
            isRenewal: false,
          })
        } else if (newVal && !newVal.invoice_number) {
          this.$store.dispatch('app/setError', 'Order submission failed.')
          this.$emit('close-modal')
        }
      },
    },
    certRenewalsAvailable: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.renewalAvailability = this.isRenewalAvailableCalculator(
            this.renewalModalStatus.certification
          )
        } else {
          this.buyActionDisabled = true
        }
      },
    },
    totalPrice: {
      immediate: true,
      handler(newVal) {
        this.handlePaymentMethodChange(false)
      },
    },
  },
  async created() {
    this.isLoading = true
    this.updateCoupon('')
    this.renewalAvailability = this.isRenewalAvailableCalculator(
      this.renewalModalStatus.certification
    )
    if (this.voucher === null) {
      if (!this.renewalAvailability.isRenewalAvailable) {
        await this.postCertRenewalsAvailable(
          this.renewalModalStatus.certification.attempt_id
        )
        this.renewalAvailability = this.isRenewalAvailableCalculator(
          this.renewalModalStatus.certification
        )
      }
    }
    if (this.renewalModalStatus.isRenewal) {
      if (this.renewalAvailability.hasRenewalCertAttempt) {
        await this.certRenewalCostPreview(
          this.renewalAvailability.hasRenewalCertAttempt.id
        )
        this.totalPrice = this.certRenewalCost.due_today.total
        this.totalTax = this.certRenewalCost.due_today.tax
      } else {
        this.buyActionDisabled = true
      }
    } else {
      this.totalPrice = this.voucher.user_receives_discount
        ? this.voucher.discounted_price
        : this.voucher.price
    }
    this.isLoading = false
  },
  methods: {
    ...mapActions({
      setRenewModalStep: 'cpeProgram/setRenewModalStep',
      setRenewModalStatus: 'cpeProgram/setRenewModalStatus',
      certRenewalCostPreview: 'certifications/certRenewalCostPreview',
      certRenewalPurchaseAction: 'certifications/certRenewalPurchase',
      postCertRenewalsAvailable: 'certifications/postCertRenewalsAvailable',
      updateCoupon: 'shoppingCart/updateCoupon',
      getCPECredits: 'cpeProgram/getCPECredits',
    }),
    handlePayment() {
      this.setRenewModalStep(STEPS.SUCCESS)
    },
    goBack() {
      if (!this.renewalModalStatus.prevStep) this.$emit('close-modal')
      this.setRenewModalStep(this.renewalModalStatus.prevStep)
    },
    async onBuyAction() {
      if (this.renewalModalStatus.isRenewal) {
        await this.handleRenewal()
      } else {
        this.handleVoucherPurchase()
      }
    },
    isAddCreditCardFormHandler(val) {
      this.isAddCreditCardForm = val
    },
    async handleRenewal(challengeToken = '') {
      this.isLoading = true
      const certificationAttemptId = this.renewalModalStatus.certification
        .attempt_id
      const certRenewalAttemptId = this.certRenewalsAvailable.find(item => {
        return item.user_exam_attempt_id === certificationAttemptId
      })
      await this.certRenewalPurchaseAction({
        cert_renewal_attempt_id: certRenewalAttemptId.id,
        billing_token: this.billingToken || '',
        payment_challenge_token: challengeToken,
      })
      if (this.certRenewalPurchaseGetter.invoice_number) {
        this.setRenewModalStatus({
          step: STEPS.SUCCESS,
          currentSelection: this.voucher,
          isRenewal: true,
        })
        this.getCPECredits()
      } else {
        this.$store.dispatch('app/setError', 'Renewal failed.')
        this.$emit('close-modal')
      }
      this.isLoading = false
    },
    handleVoucherPurchase() {
      this.$refs.invisibleRecaptcha.execute()
    },
    isCreatingPaypalInstance(val) {
      this.isLoading = val
    },
    handlePaymentMethodChange(value) {
      if (this.totalPrice <= 0) {
        this.buyActionDisabled = true
        return
      }
      this.buyActionDisabled = value
    },
  },
}
</script>

<style lang="scss" scoped>
.renew-modal-payment {
  &__container {
    display: flex;
    flex-direction: column;
    gap: 0;
  }
  &__header {
    display: flex;
    flex-direction: column;
    align-items: center;
    &--hide {
      display: none;
    }
  }
  &__title {
    font-size: rem(32);
    line-height: rem(40);
    margin: 0;
    text-align: center;
    @include mobile {
      font-size: rem(40);
      line-height: rem(52);
      margin: 0;
    }
  }
  &__subtitle,
  &__info {
    color: var(--white);
    font-size: rem(16px);
    font-style: normal;
    font-weight: 700;
    line-height: rem(24px);
    &-wrapper {
      margin: 20px 0;
      max-width: 630px;
    }
    &--greyed {
      color: var(--light-blue-gray);
      font-size: rem(16);
      line-height: rem(24);
      text-align: center;
    }
    &-higher-cert {
      color: var(--light-blue-gray);
      text-align: center;
      font-size: rem(18px);
      font-style: normal;
      font-weight: 500;
      line-height: rem(24px);
      display: flex;
      flex-flow: column;
      gap: 20px;
    }
    &-fee {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 20px;
      border-radius: 12px;
      background: var(--glass-mid-gray);
      margin-top: 20px;

      &-legend {
        font-size: rem(18px);
        font-style: normal;
        font-weight: 500;
        line-height: rem(24px);
        color: var(--white);
      }
      &-amount {
        color: var(--white);
        font-size: rem(20px);
        font-style: normal;
        font-weight: 700;
        line-height: rem(26px);
        &-subtitle {
          font-size: rem(14px);
          font-style: normal;
          font-weight: 450;
          line-height: rem(20px);
          color: var(--light-blue-gray);
        }
      }
    }
  }
  &__cta-wrapper {
    display: flex;
  }
  &__cart {
    position: relative;
    left: -30px;
    bottom: -30px;
  }
  &__payment-flow {
    display: flex;
    justify-content: center;
  }
  &__footer {
    margin-top: 20px;
    &--hide {
      display: none;
    }
  }
}
</style>
